import { useState } from "react";
import { toast } from "sonner";
import { Wand2, Copy as Dup } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { useMe, can } from "@/context/MeContext";

const CATS = ["Médecins", "Chirurgiens-dentistes", "Paramédicaux", "Pharmacies", "Laboratoires & imagerie", "Établissements de soins"];

const Suggestion = ({ s, writer, onApply }) => (
  <div className="py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid={`ai-suggestion-${s.id}`}>
    <div className="min-w-0 flex-1">
      <p className="font-medium text-stone-900">{s.name}</p>
      <p className="text-xs text-stone-500">{s.current.subcategory} → <strong className="text-[#C2410C]">{s.category} › {s.subcategory}</strong> · confiance {s.confidence}{s.reason ? ` · ${s.reason}` : ""}</p>
    </div>
    {writer && <Button size="sm" variant="outline" disabled={s.applied} onClick={() => onApply(s)} data-testid={`ai-apply-${s.id}`}>{s.applied ? "Appliqué" : "Appliquer"}</Button>}
  </div>
);

export const DirectoryQuality = () => {
  const api = useApi();
  const writer = can(useMe(), "write:directory");
  const [consent, setConsent] = useState(false);
  const [category, setCategory] = useState("");
  const [res, setRes] = useState(null);
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);
  const review = async (offset = 0) => {
    setBusy(true);
    try { setRes(await api("post", "/ai/directory/review", { category: category || null, limit: 25, offset, consent })); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const apply = async (s) => {
    try {
      await api("put", `/sp/directory/${s.id}`, { category: s.category, subcategory: s.subcategory });
      setRes((r) => ({ ...r, suggestions: r.suggestions.map((x) => (x.id === s.id ? { ...x, applied: true } : x)) }));
      toast.success("Fiche reclassée dans Sehatic-Pedia");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="ai-quality-panel">
      <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-4 text-sm">
        <h3 className="font-heading text-base font-bold text-stone-900">Reclassement suggéré par Claude</h3>
        <p className="text-stone-600">Sans catégorie choisie, Claude examine les fiches « Autre spécialiste » et « Autre paramédical ». Vous validez chaque correction.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="ai-quality-category"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Fiches « Autre … »</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => review(0)} disabled={busy || !consent} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid="ai-quality-run"><Wand2 className="h-4 w-4 mr-1.5" /> {busy ? "Analyse…" : "Analyser 25 fiches"}</Button>
        </div>
        <label className="flex items-start gap-2 text-stone-700"><Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="ai-quality-consent" className="mt-0.5" />
          J'accepte d'envoyer à Claude le nom et la catégorie de ces fiches publiques de l'annuaire (aucun téléphone, adresse ni donnée patient).</label>
        {res && (
          <div className="divide-y divide-stone-100" data-testid="ai-quality-results">
            <p className="pb-2 text-stone-500">Fiches {res.reviewed ? `${res.offset + 1}–${res.offset + res.reviewed}` : "0"} sur {res.total} examinées — {res.suggestions.length} correction(s) proposée(s)</p>
            {res.suggestions.map((s) => <Suggestion key={s.id} s={s} writer={writer} onApply={apply} />)}
            {res.next_offset != null && <div className="pt-3"><Button variant="outline" size="sm" disabled={busy || !consent} onClick={() => review(res.next_offset)} data-testid="ai-quality-next">Analyser les 25 suivantes</Button></div>}
          </div>
        )}
      </div>
      <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-3 text-sm">
        <div className="flex items-center"><h3 className="font-heading text-base font-bold text-stone-900">Doublons probables</h3>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => api("get", "/ai/directory/duplicates").then(setDups).catch((e) => toast.error(errMsg(e)))} data-testid="ai-dups-run"><Dup className="h-4 w-4 mr-1.5" /> Rechercher</Button></div>
        <p className="text-stone-500">Détection automatique (même téléphone, ou même nom dans la même wilaya), sans envoi à Claude. Corrigez ou supprimez depuis la page Annuaire.</p>
        {dups && (dups.groups.length === 0 ? <p data-testid="ai-dups-empty">Aucun doublon détecté.</p> : (
          <div className="divide-y divide-stone-100" data-testid="ai-dups-list">
            {dups.groups.map((g, i) => (
              <div key={i} className="py-2.5"><p className="text-xs uppercase tracking-[0.1em] text-[#C2410C]">{g.reason}</p>
                {g.items.map((it) => <p key={it.id} className="text-stone-700">{it.name} <span className="text-stone-400">· {it.subcategory} · {it.phone || "—"} · {it.address || "—"}</span></p>)}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
